import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import grapesjs from 'grapesjs';
import newsletterPlugin from 'grapesjs-preset-newsletter';
import 'grapesjs/dist/css/grapes.min.css';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSave, faEye, faArrowLeft, faCopy, faEnvelope,
    faComment, faMobileAlt, faLock,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import InlineLoader from '../components/Loader/InlineLoader';
import { useAuth } from '../context/AuthProvider';
import './TemplateBuilder.scss';
import { API_BASE_URL } from '../api';

const getCanalOptions = (t) => [
    { value: 'email',    label: t('Email'),    icon: faEnvelope  },
    { value: 'whatsapp', label: t('WhatsApp'), icon: faComment   },
    { value: 'sms',      label: t('SMS'),      icon: faMobileAlt },
];

const getPharmacyBlocks = (t) => [
    {
        id: 'pharma-header',
        label: t('template_builder.block.header'),
        category: t('template_builder.block.category_pharmacy'),
        media: `<svg viewBox="0 0 40 20" xmlns="http://www.w3.org/2000/svg"><rect fill="#005A8E" width="40" height="20" rx="2"/><text x="20" y="13" text-anchor="middle" fill="white" font-size="7" font-family="Arial">PHARMACIE</text></svg>`,
        content: `<div style="background:#005A8E;padding:24px 32px;text-align:center;"><h1 style="color:white;font-family:Arial,sans-serif;margin:0;font-size:24px;">{nom_pharmacie}</h1></div>`,
    },
    {
        id: 'pharma-promo-banner',
        label: t('template_builder.block.promo_banner'),
        category: t('template_builder.block.category_pharmacy'),
        media: `<svg viewBox="0 0 40 20" xmlns="http://www.w3.org/2000/svg"><rect fill="#fef3c7" width="40" height="20" rx="2"/><text x="20" y="13" text-anchor="middle" fill="#92400e" font-size="7" font-family="Arial" font-weight="bold">{offre}</text></svg>`,
        content: `<div style="background:#fef3c7;padding:16px;text-align:center;border-left:4px solid #f59e0b;"><p style="font-family:Arial,sans-serif;font-size:18px;font-weight:bold;color:#92400e;margin:0;">{offre}</p></div>`,
    },
    {
        id: 'pharma-whatsapp-btn',
        label: t('template_builder.block.whatsapp_button'),
        category: t('template_builder.block.category_pharmacy'),
        media: `<svg viewBox="0 0 40 20" xmlns="http://www.w3.org/2000/svg"><rect fill="#25D366" width="40" height="20" rx="10"/><text x="20" y="13" text-anchor="middle" fill="white" font-size="7" font-family="Arial">WhatsApp</text></svg>`,
        content: `<div style="text-align:center;padding:16px;"><a href="https://wa.me/{telephone}" style="background:#25D366;color:white;padding:12px 28px;border-radius:8px;text-decoration:none;display:inline-block;font-family:Arial,sans-serif;font-weight:bold;font-size:14px;">💬 Nous contacter sur WhatsApp</a></div>`,
    },
    {
        id: 'pharma-horaires',
        label: t('template_builder.block.hours'),
        category: t('template_builder.block.category_pharmacy'),
        media: `<svg viewBox="0 0 40 20" xmlns="http://www.w3.org/2000/svg"><rect fill="#f3f4f6" width="40" height="20" rx="2"/><text x="20" y="13" text-anchor="middle" fill="#374151" font-size="6" font-family="Arial">Lun-Sam 8h-21h</text></svg>`,
        content: `<div style="padding:16px;background:#f9fafb;border-radius:6px;font-family:Arial,sans-serif;"><p style="font-size:13px;color:#374151;margin:0;line-height:1.8;">📍 <strong>Horaires :</strong> Lun–Sam 8h00–21h00<br>📞 <strong>Adresse :</strong> {adresse_pharmacie}</p></div>`,
    },
];

const getVariableBlocks = (t) => [
    { id: 'var-prenom',       label: '{prenom}',        content: `<span style="color:#2563eb;font-weight:bold;font-family:Arial,sans-serif;">{prenom}</span>` },
    { id: 'var-nom-pharmacie',label: '{nom_pharmacie}', content: `<span style="color:#2563eb;font-weight:bold;font-family:Arial,sans-serif;">{nom_pharmacie}</span>` },
    { id: 'var-offre',        label: '{offre}',         content: `<span style="color:#dc2626;font-weight:bold;font-family:Arial,sans-serif;">{offre}</span>` },
    { id: 'var-date',         label: '{date}',          content: `<span style="color:#059669;font-weight:bold;font-family:Arial,sans-serif;">{date}</span>` },
    { id: 'var-lien',         label: '{lien}',          content: `<a href="{lien}" style="color:#2563eb;font-family:Arial,sans-serif;">{lien}</a>` },
];

export default function TemplateBuilder() {
    const { t }    = useTranslation();
    const { id }   = useParams();
    const navigate = useNavigate();
    const isNew    = !id;

    const { setTitle } = usePageTitle();
    const { hasRight } = useAuth();
    const canCreate = hasRight('MARKETING_CREATE');
    const canEdit = hasRight('MARKETING_EDIT');
    const canSave = isNew ? canCreate : canEdit;

    const CANAL_OPTIONS = getCanalOptions(t);

    const editorRef         = useRef(null);
    const editorInstanceRef = useRef(null);

    const [template, setTemplate] = useState(null);
    const [nom,      setNom]      = useState(t('marketing_templates.action.new'));
    const [canal,    setCanal]    = useState('email');
    const [loading,  setLoading]  = useState(!isNew);
    const [saving,   setSaving]   = useState(false);
    const [preview,  setPreview]  = useState(null); // { html, text } | null
    const [leftTab,  setLeftTab]  = useState('blocks');

    // ── Fetch template ───────────────────────────────────────────────────────
    useEffect(() => {
        if (isNew) {
            setTitle(t('marketing_templates.action.new'));
            return;
        }
        axios.get(`${API_BASE_URL}/api/v1/marketing/templates/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setTemplate(data);
                setNom(data.nom);
                setCanal(data.canal);
                setTitle(data.nom);
                document.title = data.nom;
            })
            .catch(() => toast.error(t('template_builder.toast.not_found')))
            .finally(() => setLoading(false));
    }, [id, isNew, t]);

    // ── Init GrapesJS (non-system templates only) ────────────────────────────
    useEffect(() => {
        if (loading) return;
        if (template?.isSystem) return; // system = iframe preview, no editor
        if (editorInstanceRef.current) return;
        if (!editorRef.current) return;

        const editor = grapesjs.init({
            container: editorRef.current,
            height: '100%',
            width: 'auto',
            fromElement: false,
            storageManager: false,
            plugins: [newsletterPlugin],
            pluginsOpts: { [newsletterPlugin]: {} },
            blockManager: { appendTo: '#gjs-blocks-panel' },
            styleManager: { appendTo: '#gjs-styles-panel' },
            layerManager: { appendTo: '#gjs-layers-panel' },
            traitManager: { appendTo: '#gjs-traits-panel' },
            panels: { defaults: [] },
        });

        // Add custom pharmacy blocks
        [...getPharmacyBlocks(t), ...getVariableBlocks(t).map(b => ({ ...b, category: t('template_builder.block.category_variables') }))].forEach(b => {
            editor.BlockManager.add(b.id, b);
        });

        // Load existing project data
        if (template?.gjsComponents) {
            try {
                editor.loadProjectData(JSON.parse(template.gjsComponents));
            } catch {
                if (template.htmlContent) editor.setComponents(template.htmlContent);
            }
        } else if (template?.htmlContent) {
            editor.setComponents(template.htmlContent);
        }

        editorInstanceRef.current = editor;

        return () => {
            editor.destroy();
            editorInstanceRef.current = null;
        };
    }, [loading, template, t]);

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        const editor = editorInstanceRef.current;
        if (!editor) return;
        setSaving(true);

        const projectData = JSON.stringify(editor.getProjectData());
        const html        = editor.getHtml();
        const css         = editor.getCss();
        const htmlContent = `<html><head><style>${css}</style></head><body>${html}</body></html>`;
        const textContent = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

        const payload = { nom, canal, gjsComponents: projectData, htmlContent, textContent };

        try {
            if (isNew) {
                const { data } = await axios.post(
                    `${API_BASE_URL}/api/v1/marketing/templates`, payload, { withCredentials: true }
                );
                toast.success(t('template_builder.toast.created'));
                navigate(`/marketing/templates/${data.id}/edit`, { replace: true });
            } else {
                await axios.put(
                    `${API_BASE_URL}/api/v1/marketing/templates/${id}`, payload, { withCredentials: true }
                );
                toast.success(t('template_builder.toast.saved'));
            }
        } catch {
            toast.error(t('template_builder.toast.save_error'));
        } finally {
            setSaving(false);
        }
    }, [nom, canal, id, isNew, navigate, t]);

    // ── Duplicate ────────────────────────────────────────────────────────────
    const handleDuplicate = useCallback(async () => {
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/api/v1/marketing/templates/${id}/duplicate`,
                {},
                { withCredentials: true }
            );
            toast.success(t('template_builder.toast.duplicated'));
            navigate(`/marketing/templates/${data.id}/edit`);
        } catch {
            toast.error(t('marketing_templates.toast.duplicate_error'));
        }
    }, [id, navigate, t]);

    // ── Preview ──────────────────────────────────────────────────────────────
    const handlePreview = useCallback(() => {
        const editor = editorInstanceRef.current;
        if (!editor && !template) return;

        let html, text;
        if (editor) {
            const rawHtml = editor.getHtml();
            const css     = editor.getCss();
            html = `<html><head><style>${css}</style></head><body>${rawHtml}</body></html>`;
            text = rawHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        } else {
            html = template?.htmlContent ?? '';
            text = template?.textContent ?? '';
        }
        setPreview({ html, text });
    }, [template]);

    // ── Close preview with Escape ────────────────────────────────────────────
    useEffect(() => {
        const handler = e => { if (e.key === 'Escape') setPreview(null); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    if (loading) return <InlineLoader loadingMessage={t('template_builder.loading')} />;

    const isSystem = Boolean(template?.isSystem);
    const canalMeta = CANAL_OPTIONS.find(o => o.value === canal) ?? CANAL_OPTIONS[0];

    return (
        <div className="builder-page">

            {/* ── Toolbar ── */}
            <div className="builder-toolbar">
                <div className="builder-toolbar-left">
                    {!isSystem && (
                        <input
                            className="builder-nom-input"
                            value={nom}
                            onChange={e => setNom(e.target.value)}
                            placeholder={t('template_builder.field.name_placeholder')}
                        />
                    )}

                    <div className="builder-canal-select">
                        {CANAL_OPTIONS.map(o => (
                            <button
                                key={o.value}
                                className={`builder-canal-btn ${canal === o.value ? 'active' : ''}`}
                                onClick={() => !isSystem && setCanal(o.value)}
                                disabled={isSystem}
                                title={o.label}
                            >
                                <FontAwesomeIcon icon={o.icon} /> {o.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="builder-toolbar-right">
                    <button className="builder-btn builder-btn--ghost" onClick={handlePreview}>
                        <FontAwesomeIcon icon={faEye} /> {t('template_builder.action.preview')}
                    </button>
                    {isSystem ? (
                        canCreate && (
                            <button className="builder-btn builder-btn--primary" onClick={handleDuplicate}>
                                <FontAwesomeIcon icon={faCopy} /> {t('template_builder.action.duplicate_edit')}
                            </button>
                        )
                    ) : (
                        canSave && (
                            <button className="builder-btn builder-btn--primary" onClick={handleSave} disabled={saving}>
                                <FontAwesomeIcon icon={faSave} /> {saving ? t('template_builder.action.saving') : t('template_builder.action.save')}
                            </button>
                        )
                    )}
                </div>
            </div>

            {/* ── System banner ── */}
            {isSystem && (
                <div className="builder-system-banner">
                    <FontAwesomeIcon icon={faLock} />
                    {t('template_builder.banner.system_prefix')} <strong>{t('template_builder.action.duplicate_edit')}</strong> {t('template_builder.banner.system_suffix')}
                </div>
            )}

            {/* ── Editor body ── */}
            {isSystem ? (
                // System template: iframe preview
                <div className="builder-iframe-wrapper">
                    {template?.htmlContent ? (
                        <iframe
                            srcDoc={template.htmlContent}
                            className="builder-iframe"
                            title={t('template_builder.preview.iframe_title')}
                            sandbox="allow-same-origin"
                        />
                    ) : (
                        <div className="builder-empty">
                            <p>{t('template_builder.empty.no_content')}</p>
                            {canCreate && (
                                <button className="builder-btn builder-btn--primary" onClick={handleDuplicate}>
                                    <FontAwesomeIcon icon={faCopy} /> {t('template_builder.action.duplicate_edit')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                // Editable template: full GrapesJS
                <div className="builder-editor-body">
                    <div className="builder-panel builder-panel--left">
                        <div className="builder-panel-tabs">
                            <button className={`builder-tab ${leftTab === 'blocks' ? 'active' : ''}`} onClick={() => setLeftTab('blocks')}>{t('template_builder.tab.blocks')}</button>
                            <button className={`builder-tab ${leftTab === 'layers' ? 'active' : ''}`} onClick={() => setLeftTab('layers')}>{t('template_builder.tab.layers')}</button>
                        </div>
                        <div id="gjs-blocks-panel" className="builder-panel-content" style={{ display: leftTab === 'blocks' ? 'block' : 'none' }} />
                        <div id="gjs-layers-panel" className="builder-panel-content" style={{ display: leftTab === 'layers' ? 'block' : 'none' }} />
                    </div>

                    <div ref={editorRef} className="builder-canvas" />

                    <div className="builder-panel builder-panel--right">
                        <div className="builder-panel-tabs">
                            <button className="builder-tab active">{t('template_builder.tab.styles')}</button>
                        </div>
                        <div id="gjs-traits-panel" className="builder-panel-content" />
                        <div id="gjs-styles-panel" className="builder-panel-content" />
                    </div>
                </div>
            )}

            {/* ── Preview modal ── */}
            {preview && (
                <div className="builder-preview-overlay" onClick={() => setPreview(null)}>
                    <div className="builder-preview-modal" onClick={e => e.stopPropagation()}>
                        <div className="builder-preview-header">
                            <span>{t('template_builder.preview.title_prefix')} <strong>{canalMeta.label}</strong></span>
                            <button className="builder-preview-close" onClick={() => setPreview(null)}>✕</button>
                        </div>

                        {canal === 'email' ? (
                            <iframe
                                srcDoc={preview.html}
                                className="builder-preview-iframe"
                                title={t('template_builder.preview.iframe_title')}
                                sandbox="allow-same-origin"
                            />
                        ) : (
                            <div className="builder-phone-wrap">
                                <div className="builder-phone">
                                    <div className="builder-phone-screen">
                                        <div className="builder-chat-bubble">{preview.text || t('template_builder.preview.no_content')}</div>
                                    </div>
                                </div>
                                <div className={`builder-char-count ${
                                    preview.text.length <= 160 ? 'green'
                                    : preview.text.length <= 320 ? 'orange'
                                    : 'red'
                                }`}>
                                    {t('template_builder.preview.char_count', { count: preview.text.length })}
                                    {' · '}
                                    {t('template_builder.preview.sms_count', { count: preview.text.length <= 160 ? 1 : Math.ceil(preview.text.length / 160) })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
